import { useEffect, useState } from "react";

/**
 * Animated dashboard background — pure SVG + CSS, lightweight, mobile-fast.
 * Visual story: rising sales line + growing bar chart, looping subtly forever.
 */

export default function AnimatedDashboard() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base yellow + texture */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700] via-[#FFE74A] to-[#FFD700]" />
      <div className="absolute inset-0 grain opacity-25" />

      {/* Soft left vignette so headline text stays readable */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] via-[#FFD700]/50 to-transparent" />

      {/* SVG chart — drawing animation that loops */}
      <svg
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="none"
        viewBox="0 0 800 400"
        aria-hidden
      >
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#000" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#000" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1="0"
            x2="800"
            y1={80 * i + 40}
            y2={80 * i + 40}
            stroke="#000"
            strokeOpacity="0.06"
            strokeDasharray="4 6"
          />
        ))}
        <path
          className="hero-area"
          d="M 0 360 L 60 340 L 140 320 L 220 305 L 300 270 L 380 250 L 460 200 L 540 180 L 620 130 L 720 90 L 800 60 L 800 400 L 0 400 Z"
          fill="url(#areaGrad)"
        />
        <path
          className="hero-line-loop"
          d="M 0 360 L 60 340 L 140 320 L 220 305 L 300 270 L 380 250 L 460 200 L 540 180 L 620 130 L 720 90 L 800 60"
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {[
          [60, 340], [140, 320], [220, 305], [300, 270], [380, 250],
          [460, 200], [540, 180], [620, 130], [720, 90], [800, 60],
        ].map(([cx, cy], i) => (
          <circle
            key={i}
            className="hero-dot"
            cx={cx}
            cy={cy}
            r="4"
            fill="#000"
            style={{ animationDelay: `${0.6 + i * 0.12}s`, animationFillMode: "forwards" }}
          />
        ))}
      </svg>

      {/* Bar chart cluster — bottom right, larger and more prominent */}
      <div className="pointer-events-none absolute bottom-6 right-4 z-[1] hidden h-40 w-64 md:block lg:bottom-12 lg:right-12 lg:h-56 lg:w-96">
        <div className="absolute inset-0 flex items-end justify-between gap-1.5">
          {[35, 48, 42, 65, 58, 78, 70, 92, 85, 100, 88, 95].map((h, i) => (
            <div
              key={i}
              className="hero-bar w-full origin-bottom border-2 border-black bg-black"
              style={{ height: `${h}%`, animationDelay: `${0.8 + i * 0.07}s`, animationFillMode: "forwards" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Live ticker widget for use INSIDE the hero right column.
 * Replaces the static "Watch Demo" card with a live-updating dashboard look.
 */
export function LiveTicker() {
  const [gmv, setGmv] = useState(0);
  const [orders, setOrders] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const duration = 2400;
    const tick = () => {
      const elapsed = Date.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setGmv(Math.round(eased * 524));
      setOrders(Math.round(eased * 18420));
      if (t < 1) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="card-brutal bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-black/60">Live · Last 24h</div>
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" /> ACTIVE
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="border-r-2 border-black pr-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-black/60">Managed GMV</div>
          <div className="display mt-1 text-3xl font-black tabular-nums">
            ₹{gmv}<span className="text-base">Cr+</span>
          </div>
          <div className="mt-0.5 text-[10px] font-bold text-emerald-700">▲ 38% MoM</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-black/60">Orders / Day</div>
          <div className="display mt-1 text-3xl font-black tabular-nums">
            {orders.toLocaleString("en-IN")}
          </div>
          <div className="mt-0.5 text-[10px] font-bold text-black/60">across 10 marketplaces</div>
        </div>
      </div>
      {/* Mini sparkline */}
      <svg viewBox="0 0 200 40" className="mt-3 h-10 w-full" aria-hidden>
        <polyline
          points="0,32 20,28 40,30 60,22 80,24 100,16 120,18 140,10 160,12 180,6 200,4"
          fill="none"
          stroke="#000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points="0,32 20,28 40,30 60,22 80,24 100,16 120,18 140,10 160,12 180,6 200,4 200,40 0,40"
          fill="#FFD700"
          fillOpacity="0.5"
          stroke="none"
        />
      </svg>
    </div>
  );
}
